import { type FormEvent, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { isToday, parseISO, startOfDay } from 'date-fns';
import { Icon } from '@/shared/ui/Icon';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { Badge } from '@/shared/ui/Badge';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { SkeletonCard } from '@/shared/ui/Skeleton';
import { ErrorState } from '@/shared/ui/ErrorState';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Dialog } from '@/shared/ui/Dialog';
import { AvatarStack } from '@/shared/ui/AvatarStack';
import { MeetingTabs } from '@/features/meeting/MeetingTabs';
import { Rise } from '@/shared/ui/Rise';
import { TimelineItem } from '@/shared/ui/TimelineItem';
import { useToast } from '@/shared/ui/Toast';
import { getMeeting, listParticipants, transitionMeeting } from '@/shared/api/meetings';
import { listPlaceCandidates, type PlaceCandidate } from '@/shared/api/voting';

import { createInvitation } from '@/shared/api/invitations';
import { createRoom } from '@/shared/api/rooms';
import {
  type ScheduleItem as ScheduleItemT,
  addScheduleComment,
  createScheduleItem,
  listScheduleItems,
  listScheduleComments,
  deleteScheduleComment,
} from '@/shared/api/schedule';
import { DateTimeField } from '@/shared/ui/DateTimeField';
import { apiErrorMessage } from '@/shared/api/client';
import { fmtKst } from '@/shared/lib/date';
import { useAuthStore } from '@/shared/auth/store';
import { RouteBadge } from '@/features/route/RouteBadge';
import type { RouteCoords } from '@/features/route/useRoute';

const DOT_PALETTE = ['#46a758', '#71d083', '#d4b36a', '#8dbf6a', '#d9796a', '#89d99b'];

// 구글 캘린더 추가 링크 — 푸시 없이도 모임 리마인드를 받을 수 있는 가장 가벼운 길
function googleCalendarUrl(
  m: { id: string; title: string; confirmedDate: string; meetingPointName: string | null; region: string | null },
  items: ScheduleItemT[],
): string {
  const start = new Date(m.confirmedDate);
  const last = items[items.length - 1];
  const lastAnchor = last ? new Date(last.endAt ?? last.startAt) : start;
  // 종료: 마지막 일정 기준 +2시간 (캘린더는 종료 시각이 필수)
  const end = new Date(Math.max(lastAnchor.getTime(), start.getTime()) + 2 * 3600_000);
  const fmt = (d: Date) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: m.title,
    dates: `${fmt(start)}/${fmt(end)}`,
    details: `모이자 모임 — https://moiza.me/meetings/${m.id}`,
    location: m.meetingPointName ?? m.region ?? '',
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function timeRange(item: ScheduleItemT): string {
  const start = fmtKst(item.startAt, 'HH:mm');
  if (!item.endAt) return start;
  return `${start} ~ ${fmtKst(item.endAt, 'HH:mm')}`;
}

function coordsFromPlace(place: PlaceCandidate | null | undefined): RouteCoords | null {
  if (!place?.x || !place.y) return null;
  return { x: place.x, y: place.y };
}

function RouteTransition({ origin, dest }: { origin: RouteCoords; dest: RouteCoords }) {
  return (
    <span className="block whitespace-nowrap">
      <RouteBadge origin={origin} dest={dest} initialMode="walk" compact />
    </span>
  );
}

export default function MeetingPage() {
  const { id = '' } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const toast = useToast();
  const navigate = useNavigate();
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [membersOpen, setMembersOpen] = useState(false);
  const [settleConfirm, setSettleConfirm] = useState(false);
  const [addingItem, setAddingItem] = useState(false);
  const [itemTitle, setItemTitle] = useState('');
  const [itemStart, setItemStart] = useState('');
  const [itemMemo, setItemMemo] = useState('');
  const [useFirstScheduleAsPoint, setUseFirstScheduleAsPoint] = useState(false);
  const [commentingItem, setCommentingItem] = useState<ScheduleItemT | null>(null);
  const [commentText, setCommentText] = useState('');

  const meetingQ = useQuery({
    queryKey: ['meeting', id],
    queryFn: () => getMeeting(id),
    enabled: !!id,
  });
  const partsQ = useQuery({
    queryKey: ['participants', id],
    queryFn: () => listParticipants(id),
    enabled: !!id,
  });
  const scheduleQ = useQuery({
    queryKey: ['schedule', id],
    queryFn: () => listScheduleItems(id),
    enabled: !!id,
  });
  // 코스 구간 경로용 — 일정의 placeCandidateId 를 장소 좌표로 푼다
  const placesQ = useQuery({
    queryKey: ['places', id],
    queryFn: () => listPlaceCandidates(id),
    enabled: !!id,
  });
  const commentsQ = useQuery({
    queryKey: ['schedule-comments', commentingItem?.id],
    queryFn: () => listScheduleComments(commentingItem?.id ?? ''),
    enabled: Boolean(commentingItem?.id),
  });
  const commentCountsQ = useQueries({
    queries: (scheduleQ.data ?? []).map((item) => ({
      queryKey: ['schedule-comments', item.id],
      queryFn: () => listScheduleComments(item.id),
      enabled: Boolean(item.id),
      staleTime: 30_000,
    })),
  });

  const inviteMut = useMutation({
    mutationFn: () => createInvitation(id),
    onSuccess: async (inv) => {
      const token = inv.shareUrl.split('/').pop() ?? '';
      const resolvedUrl = import.meta.env.PROD
        ? inv.shareUrl
        : `${window.location.origin}/invite/${token}`;
      setShareUrl(resolvedUrl);
      setMembersOpen(true);
      try {
        if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(resolvedUrl);
          toast.success('초대 링크를 복사했어요. 아래서 다시 복사할 수도 있어요.');
        } else {
          toast.show('초대 링크가 생성됐어요. 아래서 복사해주세요.');
        }
      } catch {
        toast.show('초대 링크를 생성했어요. 아래서 복사해주세요.');
      }
    },
    onError: () => toast.error('초대 링크 생성에 실패했어요.'),
  });
  const roomMut = useMutation({
    mutationFn: () =>
      createRoom({ name: meetingQ.data?.title ?? '우리 모임방', fromMeetingId: id }),
    onSuccess: (room) => {
      qc.invalidateQueries({ queryKey: ['rooms'] });
      toast.success('모임방을 만들었어요. 참여자 전원이 멤버가 됐어요.');
      navigate(`/rooms/${room.id}`);
    },
    onError: () => toast.error('모임방 생성에 실패했어요.'),
  });
  const settleMut = useMutation({
    mutationFn: () => transitionMeeting(id, 'SETTLING'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['meeting', id] });
      setSettleConfirm(false);
      toast.success('정산을 시작했어요.');
    },
    onError: () => toast.error('정산 전환에 실패했어요.'),
  });
  const addScheduleMut = useMutation({
    mutationFn: () => {
      const memo = itemMemo.trim();
      return createScheduleItem(
        id,
        memo
          ? { title: itemTitle.trim(), startAt: new Date(itemStart).toISOString(), memo }
          : { title: itemTitle.trim(), startAt: new Date(itemStart).toISOString() },
      );
    },
    onSuccess: (created) => {
      const shouldPickPoint = useFirstScheduleAsPoint && !meetingQ.data?.meetingPointName;
      qc.invalidateQueries({ queryKey: ['schedule', id] });
      setAddingItem(false);
      setItemTitle('');
      setItemStart('');
      setItemMemo('');
      setUseFirstScheduleAsPoint(false);
      if (shouldPickPoint) {
        toast.success('첫 장소를 정하면 집결지도 같이 설정돼요.');
        navigate(`/meetings/${id}/places?mode=map&schedule=${created.id}&asMeetingPoint=1`);
        return;
      }
      toast.success('일정을 추가했어요.');
    },
    onError: (err) => toast.error(apiErrorMessage(err, '일정 추가에 실패했어요.')),
  });
  const addCommentMut = useMutation({
    mutationFn: () => {
      if (!commentingItem) throw new Error('no item');
      return addScheduleComment(commentingItem.id, commentText.trim());
    },
    onSuccess: () => {
      if (commentingItem) {
        qc.invalidateQueries({ queryKey: ['schedule-comments', commentingItem.id] });
      }
      setCommentText('');
      toast.success('댓글을 남겼어요.');
    },
    onError: (err) => toast.error(apiErrorMessage(err, '댓글 작성에 실패했어요.')),
  });
  const deleteCommentMut = useMutation({
    mutationFn: (commentId: string) => deleteScheduleComment(commentId),
    onSuccess: () => {
      if (commentingItem) {
        qc.invalidateQueries({ queryKey: ['schedule-comments', commentingItem.id] });
      }
      toast.show('댓글을 삭제했어요.');
    },
    onError: () => toast.error('댓글 삭제에 실패했어요.'),
  });

  if (meetingQ.isLoading) return <SkeletonCard />;
  if (meetingQ.isError || !meetingQ.data) {
    return <ErrorState title="모임을 불러오지 못했어요" onRetry={() => meetingQ.refetch()} />;
  }

  const m = meetingQ.data;
  const isHost = user?.id === m.hostUserId;
  const activeParts = (partsQ.data ?? []).filter((p) => !p.leftAt);
  const participantNames = activeParts.map((p) => p.nickname);

  function isScheduleStartInRange(value: string) {
    if (!m.confirmedDate) return true;
    const start = new Date(value).getTime();
    const dayStart = startOfDay(parseISO(m.confirmedDate)).getTime();
    const TWO_DAYS_MS = 2 * 86_400_000;
    return start >= dayStart && start < dayStart + TWO_DAYS_MS;
  }

  function onAddSchedule(e: FormEvent) {
    e.preventDefault();
    if (!itemTitle.trim() || !itemStart) return;
    // 직접 입력 폴백은 min/max를 우회할 수 있어 제출 시 최종 검증
    if (!isScheduleStartInRange(itemStart)) {
      toast.error('일정은 모임 당일 또는 다음 날까지만 추가할 수 있어요.');
      return;
    }
    addScheduleMut.mutate();
  }

  const items = scheduleQ.data ?? [];
  const commentCountByItemId = new Map(
    items.map((item, index) => [item.id, commentCountsQ[index]?.data?.length ?? 0]),
  );
  const places = placesQ.data ?? [];
  const placesById = new Map(places.map((place) => [place.id, place]));
  const confirmedSchedulePlaceCount = items.filter((item) => {
    if (!item.placeCandidateId) return false;
    return placesById.get(item.placeCandidateId)?.confirmed === true;
  }).length;
  const allSchedulePlacesConfirmed = items.length > 0 && confirmedSchedulePlaceCount === items.length;
  const hasDate = Boolean(m.confirmedDate);
  const hasPlace = allSchedulePlacesConfirmed;
  const progressRows = [
    {
      label: '날짜',
      title: hasDate ? fmtKst(m.confirmedDate as string, 'M월 d일 (E) HH:mm') : '날짜 후보',
      description: hasDate ? '확정' : '후보 필요',
      status: hasDate ? '완료' : '필요',
      tone: hasDate ? 'ok' : 'wait',
      to: `/meetings/${id}/vote`,
    },
    {
      label: '장소',
      title: items.length > 0 ? `${confirmedSchedulePlaceCount}/${items.length} 확정` : `${places.length}개 후보`,
      description: hasPlace ? '확정' : places.length > 0 ? '투표 중' : '후보 필요',
      status: hasPlace ? '완료' : places.length > 0 ? '진행' : '필요',
      tone: hasPlace ? 'ok' : 'wait',
      to: `/meetings/${id}/vote?tab=place`,
    },
    {
      label: '코스',
      title: items.length > 0 ? `${items.length}개 일정` : '코스 없음',
      description: items.length > 0 ? (hasPlace ? '완료' : '장소 필요') : '일정 필요',
      status: items.length === 0 ? '필요' : hasPlace ? '완료' : '진행',
      tone: items.length > 0 && hasPlace ? 'ok' : 'wait',
      to: `/meetings/${id}/places`,
    },
  ];
  const nextAction = !hasDate
    ? {
        eyebrow: '다음 할 일',
        title: '날짜 후보',
        description: `${progressRows[0].description}`,
        label: '후보 보기',
        to: `/meetings/${id}/vote`,
      }
    : items.length === 0
      ? {
          eyebrow: '다음 할 일',
          title: '일정 코스',
          description: '시간과 할 일 추가',
          label: '코스 보기',
          to: `/meetings/${id}/places`,
        }
      : !hasPlace
        ? {
            eyebrow: '다음 할 일',
            title: '장소투표',
            description: `${confirmedSchedulePlaceCount}/${items.length} 확정`,
            label: '투표 보기',
            to: `/meetings/${id}/vote?tab=place`,
          }
        : {
            eyebrow: '준비 완료',
            title: '진행 요약',
            description: '날짜 · 장소 · 코스 완료',
            label: '대화 열기',
            to: `/meetings/${id}/chat`,
          };



  // 백엔드 ScheduleService.ALLOWED_FOR_CREATE 와 동일 — 날짜 확정 전에도 코스를 짤 수 있고, 종료(CLOSED)만 불가
  const canAddSchedule = m.status !== 'CLOSED';
  const decisionLoading = scheduleQ.isLoading || placesQ.isLoading;

  // 일정 추가 기본 시각: 마지막 일정 +1시간 → 확정 날짜 → 미선택(칩에서 고름)
  function openAddSchedule() {
    if (!itemStart) {
      const last = items[items.length - 1];
      const seed = last ? (last.endAt ?? last.startAt) : m.confirmedDate;
      if (seed) {
        const d = new Date(seed);
        if (!Number.isNaN(d.getTime())) {
          if (last) d.setHours(d.getHours() + 1);
          d.setMinutes(d.getMinutes() >= 30 ? 30 : 0, 0, 0);
          setItemStart(fmtKst(d.toISOString(), "yyyy-MM-dd'T'HH:mm"));
        }
      }
    }
    setAddingItem(true);
  }


  return (
    <section className="space-y-3">
      <header className="rounded-2xl border border-surface-border/70 bg-surface px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <h1 className="line-clamp-2 min-w-0 flex-1 text-[17px] font-semibold leading-6 tracking-[-0.3px] text-ink">
            {m.title}
          </h1>
          <StatusBadge status={m.status} />
        </div>
        <div className="mt-2.5 space-y-1.5">
          <div className="flex items-center gap-2 text-[12px] font-medium leading-4 text-ink-muted">
            <Icon name="cal" size={14} className="shrink-0 text-ink-faint" />
            <span>
              {m.confirmedDate ? fmtKst(m.confirmedDate, 'M월 d일 (E) HH:mm') : '날짜 미정'}
            </span>
          </div>
          <div className="flex items-center gap-2 text-[12px] font-medium leading-4 text-ink-muted">
            <Icon name="pin" size={14} className="shrink-0 text-ink-faint" />
            <Link
              to={`/meetings/${id}/point`}
              className="min-w-0 flex-1 truncate rounded-md focus-visible:outline-none focus-visible:shadow-focus-ring"
            >
              {m.meetingPointName ?? m.region ?? '집결지 미정'}
            </Link>
            {isHost ? (
              <Link
                to={`/meetings/${id}/places?for=meeting-point`}
                className="shrink-0 text-[11px] font-semibold text-primary focus-visible:outline-none focus-visible:underline"
              >
                {m.meetingPointName ? '변경' : '설정'}
              </Link>
            ) : null}
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => setMembersOpen(true)}
            aria-label={`참여자 ${activeParts.length}명 보기`}
            className="pressable -mx-1.5 flex min-w-0 items-center gap-2 rounded-xl px-1.5 py-1 hover:bg-surface-muted focus-visible:outline-none focus-visible:shadow-focus-ring"
          >
            <AvatarStack names={participantNames} max={5} />
            <span className="truncate text-[11px] font-medium text-ink-faint">
              {activeParts.length}명 참여
            </span>
            <Icon name="right" size={14} className="shrink-0 text-ink-faint" />
          </button>
          {isHost ? (
            <button
              type="button"
              onClick={() => inviteMut.mutate()}
              disabled={inviteMut.isPending}
              aria-label="초대 링크 생성"
              className="pressable inline-flex shrink-0 items-center gap-1 rounded-lg bg-primary-soft px-2.5 py-1.5 text-[11px] font-semibold text-primary focus-visible:outline-none focus-visible:shadow-focus-ring disabled:opacity-50"
            >
              <Icon name="plus" size={13} />
              {inviteMut.isPending ? '생성 중…' : '초대'}
            </button>
          ) : null}
        </div>
      </header>

      {/* 첫 모임의 실제 첫 단계는 초대 — 혼자일 때만 노출해 다음 행동을 안내한다 */}
      {isHost && activeParts.length === 1 && m.status !== 'CLOSED' ? (
        <button
          type="button"
          onClick={() => inviteMut.mutate()}
          disabled={inviteMut.isPending}
          className="pressable flex w-full items-center gap-3 rounded-2xl bg-primary-soft px-4 py-3.5 text-left focus-visible:outline-none focus-visible:shadow-focus-ring disabled:opacity-60"
        >
          <span
            aria-hidden="true"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-surface text-primary"
          >
            <Icon name="users" size={19} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-[14px] font-semibold text-primary-text">
              아직 혼자예요
            </span>
            <span className="mt-0.5 block text-[12px] font-medium text-ink-muted">
              링크를 공유해 친구와 날짜·장소를 같이 정해보세요
            </span>
          </span>
          <span className="shrink-0 rounded-lg bg-primary px-3 py-1.5 text-[12px] font-semibold text-white">
            {inviteMut.isPending ? '생성 중…' : '초대하기'}
          </span>
        </button>
      ) : null}

      {(m.status === 'CONFIRMED' || m.status === 'ONGOING') &&
      m.confirmedDate &&
      isToday(parseISO(m.confirmedDate)) ? (
        <Link
          to={`/meetings/${id}/live`}
          className="flex items-center gap-2.5 rounded-xl bg-primary-soft px-4 py-3 text-small font-semibold text-primary-text transition-colors hover:bg-primary-soft/70"
        >
          <span className="relative flex h-2 w-2 shrink-0" aria-hidden="true">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
          </span>
          오늘 모임이 있어요 — LIVE 화면 열기
        </Link>
      ) : null}

      <MeetingTabs meetingId={id} />

      {decisionLoading ? (
        <section className="overflow-hidden rounded-2xl border border-surface-border/70 bg-surface px-4 py-4">
          <p className="text-small font-medium text-ink-muted">모임 정보를 불러오는 중…</p>
          <div className="mt-3 space-y-2" aria-hidden="true">
            <div className="h-12 rounded-xl bg-surface-muted" />
            <div className="h-12 rounded-xl bg-surface-muted" />
            <div className="h-12 rounded-xl bg-surface-muted" />
          </div>
        </section>
      ) : (
        <>
          <Rise>
          <Link
            to={nextAction.to}
            onClick={(event) => {
              if (nextAction.to === `/meetings/${id}` && items.length === 0) {
                event.preventDefault();
                openAddSchedule();
              }
            }}
            className="relative flex items-center justify-between gap-3 overflow-hidden rounded-[18px] bg-hero px-4 py-3.5 text-white shadow-[var(--hero-glow)] focus-visible:outline-none focus-visible:shadow-focus-ring"
          >
            <div
              className="absolute -right-8 -top-10 h-24 w-24 rounded-full bg-white/[0.06]"
              aria-hidden="true"
            />
            <div className="relative min-w-0">
              <p className="text-[11px] font-semibold text-white/65">{nextAction.eyebrow}</p>
              <h2 className="mt-0.5 truncate text-[18px] font-bold leading-tight tracking-[-0.5px]">
                {nextAction.title}
              </h2>
              <p className="mt-0.5 truncate text-[12px] font-medium text-white/65">
                {nextAction.description}
              </p>
            </div>
            <span className="relative shrink-0 rounded-xl bg-white/15 px-3 py-2 text-[12px] font-bold text-white">
              {nextAction.label}
            </span>
          </Link>
          </Rise>
        </>
      )}

      <section className="overflow-hidden rounded-2xl border border-surface-border/70 bg-surface">
        <div className="flex items-center justify-between gap-2 border-b border-surface-border/60 px-4 py-3">
          <h2 className="text-[15px] font-semibold leading-5 text-ink">일정 코스</h2>
          <div className="flex items-center gap-1.5">
            {m.confirmedDate ? (
              <a
                href={googleCalendarUrl({ ...m, confirmedDate: m.confirmedDate }, items)}
                target="_blank"
                rel="noopener noreferrer"
                className="pressable inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold text-ink-muted hover:bg-surface-muted focus-visible:outline-none focus-visible:shadow-focus-ring"
              >
                <Icon name="cal" size={13} />
                내 캘린더
              </a>
            ) : null}
            {canAddSchedule ? (
              <button
                type="button"
                onClick={openAddSchedule}
                aria-label="일정 추가"
                className="pressable inline-flex items-center gap-1 rounded-lg bg-primary-soft px-2.5 py-1.5 text-[11px] font-semibold text-primary focus-visible:outline-none focus-visible:shadow-focus-ring"
              >
                <Icon name="plus" size={13} />
                일정
              </button>
            ) : null}
          </div>
        </div>

        {scheduleQ.isLoading ? (
          <p className="px-4 py-5 text-body text-ink-muted">불러오는 중…</p>
        ) : scheduleQ.isError ? (
          <p className="px-4 py-5 text-body text-error" role="alert">
            일정을 불러오지 못했어요.
          </p>
        ) : items.length === 0 ? (
          <div className="px-4 py-5">
            {canAddSchedule ? (
              <EmptyState
                title="아직 일정이 없어요"
                description="일정을 추가해 코스를 만들어보세요. 날짜는 나중에 정해도 돼요."
                action={
                  <div className="flex flex-col items-center gap-2.5">
                    <Button variant="primary" onClick={openAddSchedule}>
                      <span className="inline-flex items-center gap-1.5">
                        <Icon name="plus" size={15} />
                        일정 만들기
                      </span>
                    </Button>
                    {!m.confirmedDate ? (
                      <button
                        type="button"
                        onClick={() => navigate(`/meetings/${id}/vote`)}
                        className="inline-flex items-center gap-1 text-small font-semibold text-ink-muted underline-offset-2 hover:underline focus-visible:outline-none focus-visible:underline"
                      >
                        날짜는 투표에서 정하기
                        <Icon name="right" size={14} />
                      </button>
                    ) : null}
                  </div>
                }
              />
            ) : (
              <EmptyState
                title="종료된 모임이에요"
                description="마감된 모임이라 일정을 추가할 수 없어요."
              />
            )}
          </div>
        ) : (
          <div className="px-4 py-3">
            <Link
              to={`/meetings/${id}/point`}
              className="flex items-center gap-2.5 rounded-xl pb-1 focus-visible:outline-none focus-visible:shadow-focus-ring"
            >
              <span
                className="h-[9px] w-[9px] shrink-0 rounded-full border-2 border-primary bg-surface"
                aria-hidden="true"
              />
              <span className="text-[10px] font-semibold uppercase tracking-[0.3px] text-primary">
                집결지
              </span>
              <span className="min-w-0 truncate text-[12px] font-semibold text-ink">
                {m.meetingPointName ?? m.region ?? '집결지 미정'}
              </span>
            </Link>
            <div
              className="mb-1 ml-1 h-3 w-[1.5px] rounded-full bg-gradient-to-b from-primary/25 to-transparent"
              aria-hidden="true"
            />
            <ol aria-label="모임 일정">
              {items.map((item, index) => {
                const accent = DOT_PALETTE[index % DOT_PALETTE.length];
                const nextAccent = DOT_PALETTE[(index + 1) % DOT_PALETTE.length];
                const commentCount = commentCountByItemId.get(item.id) ?? 0;
                const place = item.placeCandidateId ? placesById.get(item.placeCandidateId) : null;
                const displayTitle = place?.name ?? item.title;

                const currentCoords = coordsFromPlace(place);
                const nextItem = items[index + 1];
                const nextPlace = nextItem?.placeCandidateId
                  ? placesById.get(nextItem.placeCandidateId)
                  : null;
                const nextCoords = coordsFromPlace(nextPlace);
                const transition =
                  currentCoords && nextCoords ? (
                    <RouteTransition origin={currentCoords} dest={nextCoords} />
                  ) : null;
                const subtitle = place ? item.title : item.memo ?? undefined;

                return (
                  <TimelineItem
                    key={item.id}
                    time={timeRange(item)}
                    title={displayTitle}
                    subtitle={subtitle}
                    accent={accent}
                    nextAccent={nextAccent}
                    transition={transition}
                    isLast={index === items.length - 1}
                    to={`/meetings/${id}/schedule/${item.id}`}
                    action={
                      <button
                        type="button"
                        onClick={() => {
                          setCommentingItem(item);
                          setCommentText('');
                        }}
                        className="pressable inline-flex h-8 min-w-8 items-center justify-center gap-1 rounded-full bg-surface-muted px-2 text-[11px] font-medium text-ink-muted focus-visible:outline-none focus-visible:shadow-focus-ring"
                        aria-label={
                          commentCount > 0
                            ? `${item.title} 댓글 ${commentCount}개 열기`
                            : `${item.title} 댓글 열기`
                        }
                      >
                        <Icon name="chat" size={13} />
                        {commentCount > 0 ? commentCount : null}
                      </button>
                    }
                  />
                );
              })}
            </ol>
          </div>
        )}
      </section>

      {/* Host action — dark near-black per DESIGN.md §4 */}
      {isHost && m.status === 'ONGOING' ? (
        <Button
          variant="primary"
          onClick={() => setSettleConfirm(true)}
          disabled={settleMut.isPending}
          className="w-full"
        >
          {settleMut.isPending ? '시작 중…' : '정산 시작'}
        </Button>
      ) : null}

      {/* 참여자 시트 — 멤버 목록·초대·모임방 전환은 부가 정보라 시트로 (본문 밀도 유지) */}
      <Dialog
        open={membersOpen}
        title={`참여자 ${activeParts.length}명`}
        cancelLabel="닫기"
        onCancel={() => setMembersOpen(false)}
      >
        {partsQ.isLoading ? (
          <p className="text-body text-ink-muted">참여자 불러오는 중…</p>
        ) : partsQ.isError ? (
          <p className="text-body text-error" role="alert">
            참여자 목록을 불러오지 못했어요.
          </p>
        ) : (
          <ul className="space-y-2.5 text-body">
            {partsQ.data?.map((p) => (
              <li key={p.id} className="flex items-center justify-between">
                <span className={p.leftAt ? 'text-ink-disabled line-through' : 'text-ink'}>
                  {p.nickname}
                </span>
                <Badge tone={p.role === 'HOST' ? 'primary' : 'default'}>
                  {p.role === 'HOST' ? '모임장' : '멤버'}
                </Badge>
              </li>
            ))}
          </ul>
        )}
        {shareUrl ? (
          <>
            <Button
              variant="brand"
              className="mt-4 w-full"
              onClick={async () => {
                if (typeof navigator !== 'undefined' && navigator.share) {
                  try {
                    await navigator.share({
                      title: `${meetingQ.data?.title ?? '모임'} 초대`,
                      text: '모이자에서 날짜·장소를 같이 정해요.',
                      url: shareUrl,
                    });
                    return;
                  } catch {
                    // 공유 취소 포함 — 아래 복사 폴백으로 넘어가지 않고 조용히 종료
                    return;
                  }
                }
                try {
                  await navigator.clipboard.writeText(shareUrl);
                  toast.success('초대 링크를 복사했어요.');
                } catch {
                  toast.error('복사에 실패했어요. 다시 시도해주세요.');
                }
              }}
            >
              초대 링크 공유하기
            </Button>
            <button
              type="button"
              className="mt-2 flex min-h-10 w-full items-center justify-center gap-1.5 rounded-lg bg-primary-soft px-3 text-small font-semibold text-primary focus-visible:outline-none focus-visible:shadow-focus-ring"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(shareUrl);
                  toast.success('초대 링크를 복사했어요.');
                } catch {
                  toast.error('복사에 실패했어요. 다시 시도해주세요.');
                }
              }}
            >
              링크 복사
            </button>
          </>
        ) : null}
        {isHost ? (
          <Button
            variant={shareUrl ? 'ghost' : 'secondary'}
            className={shareUrl ? 'mt-1 w-full' : 'mt-4 w-full'}
            disabled={inviteMut.isPending}
            onClick={() => inviteMut.mutate()}
          >
            {inviteMut.isPending ? '생성 중…' : shareUrl ? '새 링크 만들기' : '초대 링크 만들기'}
          </Button>
        ) : null}
        {activeParts.length > 1 ? (
          <Button
            variant="secondary"
            className="mt-2 w-full"
            disabled={roomMut.isPending}
            onClick={() => roomMut.mutate()}
          >
            {roomMut.isPending ? '만드는 중…' : '이 멤버로 모임방 만들기'}
          </Button>
        ) : null}
      </Dialog>

      <Dialog
        open={settleConfirm}
        title="정산을 시작할까요?"
        description="정산 시작 후에는 이 모임의 상태가 '정산중'으로 바뀌어요."
        confirmLabel="정산 시작"
        onConfirm={() => settleMut.mutate()}
        confirmDisabled={settleMut.isPending}
        onCancel={() => setSettleConfirm(false)}
      />

      <Dialog
        open={addingItem}
        title="일정 추가"
        confirmLabel={addScheduleMut.isPending ? '추가 중…' : '추가'}
        confirmDisabled={
          addScheduleMut.isPending || itemTitle.trim().length === 0 || !itemStart
        }
        onConfirm={() => onAddSchedule({ preventDefault: () => {} } as FormEvent)}
        onCancel={() => {
          if (addScheduleMut.isPending) return;
          setAddingItem(false);
          setUseFirstScheduleAsPoint(false);
        }}
      >
        <form onSubmit={onAddSchedule} className="space-y-3">
          <Input
            label="제목"
            value={itemTitle}
            onChange={(e) => setItemTitle(e.target.value)}
            placeholder="예) 라멘 공방"
            required
            maxLength={80}
            autoFocus
          />
          <DateTimeField
            label="시작 시간"
            value={itemStart}
            onChange={setItemStart}
            anchorDate={m.confirmedDate ?? undefined}
          />
          <Input
            label="메모 (선택)"
            value={itemMemo}
            onChange={(e) => setItemMemo(e.target.value)}
            placeholder="점심 · 1시간"
            maxLength={120}
          />
          {!m.meetingPointName && items.length === 0 ? (
            <label className="flex items-center gap-2 rounded-xl bg-primary-soft px-3 py-2 text-small font-semibold text-ink">
              <input
                type="checkbox"
                checked={useFirstScheduleAsPoint}
                onChange={(event) => setUseFirstScheduleAsPoint(event.target.checked)}
                className="h-4 w-4 accent-primary"
              />
              첫 장소에서 바로 집결
            </label>
          ) : null}
        </form>
      </Dialog>


      <Dialog
        open={commentingItem !== null}
        title={commentingItem ? `${commentingItem.title} 댓글` : '일정 댓글'}
        cancelLabel="닫기"
        onCancel={() => {
          if (addCommentMut.isPending) return;
          setCommentingItem(null);
          setCommentText('');
        }}
      >
        <div className="space-y-3">
          {commentsQ.isLoading ? (
            <p className="rounded-2xl bg-surface-muted px-4 py-5 text-center text-body text-ink-muted">
              댓글을 불러오는 중…
            </p>
          ) : commentsQ.isError ? (
            <ErrorState
              title="댓글을 불러오지 못했어요"
              onRetry={() => commentsQ.refetch()}
            />
          ) : (commentsQ.data ?? []).length === 0 ? null : (
            <ul className="max-h-72 space-y-2 overflow-y-auto pr-1" aria-label="일정 댓글 목록">
              {(commentsQ.data ?? []).map((comment) => {
                const canDeleteComment = comment.userId === user?.id || isHost;
                return (
                  <li key={comment.id} className="rounded-2xl bg-surface-muted px-3 py-2.5">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-small font-semibold text-ink">{comment.nickname}</p>
                        <p className="mt-0.5 whitespace-pre-wrap break-words text-body leading-5 text-ink">
                          {comment.content}
                        </p>
                        <p className="mt-1 text-[11px] font-semibold text-ink-faint">
                          {fmtKst(comment.createdAt, 'M월 d일 HH:mm')}
                        </p>
                      </div>
                      {canDeleteComment ? (
                        <button
                          type="button"
                          onClick={() => deleteCommentMut.mutate(comment.id)}
                          disabled={deleteCommentMut.isPending}
                          className="pressable inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-ink-faint hover:bg-surface-border hover:text-error focus-visible:outline-none focus-visible:shadow-focus-ring disabled:opacity-40"
                          aria-label="댓글 삭제"
                        >
                          <Icon name="x" size={16} />
                        </button>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          <form
            className="flex items-center gap-2 rounded-[18px] border border-surface-border bg-surface-subtle p-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (commentText.trim().length === 0 || addCommentMut.isPending) return;
              addCommentMut.mutate();
            }}
          >
            <input
              value={commentText}
              onChange={(event) => setCommentText(event.target.value.slice(0, 300))}
              placeholder="짧은 댓글 남기기"
              aria-label="짧은 댓글 남기기"
              className="min-h-10 min-w-0 flex-1 bg-transparent px-2 text-body text-ink outline-none placeholder:text-ink-faint"
              maxLength={300}
            />
            <Button
              type="submit"
              variant="brand"
              disabled={commentText.trim().length === 0 || addCommentMut.isPending}
              className="min-h-10 shrink-0 px-3 text-[12px]"
            >
              {addCommentMut.isPending ? '작성 중' : '작성'}
            </Button>
          </form>
        </div>
      </Dialog>
    </section>
  );
}
